import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceArea, ReferenceLine } from 'recharts';

function getMidnightsBetweenDates(startTimestamp, endTimestamp, includeQuarters, noStepForward) {
  let startDate = new Date(startTimestamp);
  let endDate = new Date(endTimestamp);
  let midnights = [];

  // Set start date to the next midnight if it's not already at midnight
  if(noStepForward) {
    startDate.setHours(24, 0, 0, 0);
  }

  while (startDate <= endDate) {
    let formattedDate = startDate.toISOString().substring(0, 10); // Format as 'YYYY-MM-DD'
    midnights.push(`00:00 ${formattedDate.substring(8, 10)} ${startDate.toLocaleString('en', { month: 'short' })}`);
    midnights.push(`00:00 ${formattedDate.substring(8, 10)} ${startDate.toLocaleString('en', { month: 'short' })} ${startDate.getFullYear()}`);
    if(includeQuarters) {
      midnights.push(`06:00 ${formattedDate.substring(8, 10)} ${startDate.toLocaleString('en', { month: 'short' })}`);
      midnights.push(`06:00 ${formattedDate.substring(8, 10)} ${startDate.toLocaleString('en', { month: 'short' })} ${startDate.getFullYear()}`);
      midnights.push(`12:00 ${formattedDate.substring(8, 10)} ${startDate.toLocaleString('en', { month: 'short' })}`);
      midnights.push(`12:00 ${formattedDate.substring(8, 10)} ${startDate.toLocaleString('en', { month: 'short' })} ${startDate.getFullYear()}`);
      midnights.push(`18:00 ${formattedDate.substring(8, 10)} ${startDate.toLocaleString('en', { month: 'short' })}`);
      midnights.push(`18:00 ${formattedDate.substring(8, 10)} ${startDate.toLocaleString('en', { month: 'short' })} ${startDate.getFullYear()}`);
    }
    startDate.setDate(startDate.getDate() + 1); // Move to the next day
  }

  return midnights;
}

/**
 * 
 * @param boxBoundStart - To draw on the daily highlight chart or range, the start element
 * @param boxBoundEnd - To draw on the daily highlight chart or range, the end element
 */
const WaterLevelLineChart = ({ history, forecast, historicalMaximum, displayMode, children, boxBoundStart, boxBoundEnd, dataMode }) => {
  const computedDataMode = dataMode ? dataMode : 'Stage';
  const dataKeys = {
    'Stage': 'entryHeight',
    'Downstream Stage': 'entryHeight',
    'Flow': 'entryFlow',
    'WaterTemperature': 'entryTemperature'
  };
  const dataUnits = {
    'Stage': 'm',
    'Downstream Stage': 'm',
    'Flow': 'm3/s',
    'WaterTemperature': '°C'
  };
  const lineLabels = {
    'Stage': 'Measurement Height',
    'Downstream Stage': 'Measurement Height',
    'Flow': 'Flow',
    'WaterTemperature': 'Temperature'
  }

  const getInitialZoomBounds = function() {
    return {
      left: history && Array.isArray(history) && history.length > 0 ? new Date(history[0].entryTimestamp) : 0,
      right: forecast
        && Array.isArray(forecast) && forecast.length > 0
          ? new Date(forecast[forecast.length-1].entryTimestamp)
          : (history && Array.isArray(history) && history.length > 0 ? new Date(history[history.length-1].entryTimestamp) : 0)
    }
  };

  const [chartWidth, setChartWidth] = useState(0);
  const chartContainerRef = useRef(null);
  const [zoomBounds, setZoomBounds] = useState(getInitialZoomBounds())

  console.log(boxBoundStart, boxBoundEnd);
  useEffect(() => {
    const handleResize = () => {
      if (chartContainerRef.current) {
        setChartWidth(chartContainerRef.current.offsetWidth);
      }
    };

    // Set initial width
    handleResize();

    // Add resize event listener
    window.addEventListener('resize', handleResize);

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  if(!history) { return null; }

   // Function to determine if the label should be shown
  const formatXAxisTick = (tickItem) => {
    if (tickItem.startsWith('00:00')) {
      return tickItem.split('00:00')[1];
    }
    if(displayMode === "quarters") {
      if(tickItem.startsWith("06:00")) {
        return tickItem.split(' ')[0];
      }
      if(tickItem.startsWith("12:00")) {
        return tickItem.split(' ')[0];
      }
      if(tickItem.startsWith("18:00")) {
        return tickItem.split(' ')[0];
      }
    }
    return ''; // Return an empty string for non-midnight ticks
  };

  const domainLeft = new Date(zoomBounds.left);
  const domainRight = new Date(zoomBounds.right);


  let chartData = history;
  chartData = chartData.filter(xEle => {
    const entryDate = new Date(xEle.entryTimestamp);
    return entryDate >= domainLeft && entryDate <= domainRight;
  });

  let firstForecastRow = null;
  let lastForecastRow = null;
  if(forecast) {
    chartData = chartData.concat(forecast.filter(function(xEle, ix) {
      const entryDate = new Date(xEle.entryTimestamp);
      console.log(entryDate);

      if(entryDate >= domainLeft && entryDate <= domainRight) {
        if(!firstForecastRow) {
          firstForecastRow = xEle.entryTimestampFormatted;
        }
        lastForecastRow = xEle.entryTimestampFormatted;

        return xEle;
      }
    }))
  }

  console.log(firstForecastRow, lastForecastRow);

  const calculateTicks = () => {

    let start = chartData[0];
    let end = chartData[chartData.length-1];

    let startDate = new Date(start.entryTimestamp);
    let endDate = new Date(end.entryTimestamp);
    let midnights = getMidnightsBetweenDates(startDate, endDate, displayMode==="quarters");
    return midnights;
  };

  console.log(forecast && forecast.length > 0 && firstForecastRow && lastForecastRow ? <ReferenceArea x1={firstForecastRow} x2={lastForecastRow} stroke="black" strokeOpacity={0.3} /> : null);

  let returnValue = null;
  try {
    returnValue = (
      <div ref={chartContainerRef} style={{ width: '100%', height: '300px' }}>
        <LineChart
          width={chartWidth}
          height={300}
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="entryTimestampFormatted"
            tickFormatter={formatXAxisTick}
            ticks={calculateTicks()}
            interval="preserveStartEnd"
            domain={["dataMin", "dataMax"]}
            series="time"
          />
          <YAxis
            unit={dataUnits[computedDataMode]}
            name="Measurement Height"
          />
          {/*forecast && <ReferenceLine x={forecast[0].entryTimestampFormatted} stroke="black" label={null} />*/}
          {forecast && forecast.length > 0 && firstForecastRow && lastForecastRow ? <ReferenceArea x1={firstForecastRow} x2={lastForecastRow} stroke="black" strokeOpacity={0.3} /> : null}
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey={dataKeys[computedDataMode]} stroke="#A7C7E7" activeDot={{ r: 8 }} name={lineLabels[computedDataMode]} />
          <ReferenceLine y={historicalMaximum} isAnimationActive={false} label={{ value: historicalMaximum + 'm', position: 'left' }} stroke="red" />
          {boxBoundStart ? <ReferenceLine x={boxBoundStart.entryTimestampFormatted} isAnimationActive={false} stroke="black" /> : null}
          {boxBoundEnd ? <ReferenceLine x={boxBoundEnd.entryTimestampFormatted} isAnimationActive={false} stroke="black" /> : null}
          {(boxBoundStart && boxBoundEnd) ? <ReferenceArea x1={boxBoundStart.entryTimestampFormatted} x2={boxBoundEnd.entryTimestampFormatted} fillOpacity={0.35} /> : null}
          {children ? children : null}
        </LineChart>
      </div>
    );
  } catch(error) {}
  return returnValue;
};

export default WaterLevelLineChart;